import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/divisions", label: "Divisions" },
  { to: "/journey", label: "Journey" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-[#F7F5F0]/85 backdrop-blur-xl border-b border-foreground/10"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="group flex items-center" data-testid="navbar-logo-link">
          <img src="/assets/skn-logo.webp" alt="SKN Group of Companies" className="h-10 md:h-12 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="navbar-desktop-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-foreground font-medium" : "text-muted-foreground hover:text-[#B8997A]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="navbar-cta-button"
            className="bg-foreground text-background text-sm px-6 py-2.5 hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors duration-300"
          >
            Get in Touch
          </Link>
          <Link
            to={user ? "/portal" : "/login"}
            data-testid="navbar-employee-login"
            title={user ? "Employee Portal" : "Employee Login"}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#B8997A] transition-colors duration-300"
          >
            <UserRound size={14} strokeWidth={1.5} />
            {user ? "Portal" : "Employee Login"}
          </Link>
        </nav>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <nav
          className="md:hidden bg-[#F7F5F0] border-t border-foreground/10 px-6 py-8 flex flex-col gap-6"
          data-testid="navbar-mobile-menu"
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-display text-2xl ${isActive ? "text-[#B8997A] italic" : "text-foreground"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to={user ? "/portal" : "/login"}
            data-testid="mobile-nav-employee-login"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground"
          >
            <UserRound size={15} strokeWidth={1.5} />
            {user ? "Employee Portal" : "Employee Login"}
          </Link>
        </nav>
      )}
    </header>
  );
};
